import { apiUrl, storefrontAccessToken } from "../config";

// Fetches multiple products by their IDs and formats the response
// Returns an array of product objects with relevant details
// Each product object contains id, title, imgUrl, imgAltText, price, variant etc
async function fetchProducts(productIds) {
  const gids = productIds.map(id => `gid://shopify/Product/${id}`);
  const countryCode = window.Shopify.country;
  const language = (window.Shopify?.locale || 'en').toUpperCase().replace('-', '_');

  const query = `
    query getMultipleProducts($ids: [ID!]!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
      nodes(ids: $ids) {
        ... on Product {
          id
          handle
          title
          variants(first: 1) {
            nodes {
              id
              sku
              availableForSale
              price {
                amount
              }
              compareAtPrice {
                amount
              }
            }
          }
          featuredImage {
            url
            altText
          }
          sellingPlanGroups(first: 1) {
            nodes {
              name
              sellingPlans(first: 1) {
                nodes {
                  id
                  name                  
                  priceAdjustments {
                    adjustmentValue {
                      ... on SellingPlanPercentagePriceAdjustment {
                        adjustmentPercentage
                      }
                    } 
                  }
                }
              }
            }
          }
          supporting_tag_line: metafield(namespace: "custom", key: "supporting_tag_line") {
            value
          }
        }
      }
    }  
  `;

  const response = await fetch(`${apiUrl}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": `${storefrontAccessToken}`
    },
    body: JSON.stringify({
      query,
      variables: { ids: gids, country: countryCode, language: language }
    })
  });

  const result = await response.json();
  const formattedProducts = [];
  const nodes = (result?.data?.nodes ?? []).filter(Boolean); // drop nulls

  for (const node of nodes) {
    // Cards add the first variant that is in stock (a sold-out first flavour
    // used to make every add from the card fail silently), falling back to
    // the first variant so a fully sold-out product still renders.
    const variants = node.variants?.nodes || [];
    const variant = variants.find((v) => v?.availableForSale) || variants[0];
    const price = variant?.price.amount || null;
    const handle = node.handle || null;
    const sku = variant?.sku || null;
    const variantId = variant?.id || null;
    const compareAtPrice = variant?.compareAtPrice?.amount || null;
    const availableForSale = variant?.availableForSale ?? true;
    const sellingPlanId = node.sellingPlanGroups.nodes[0]?.sellingPlans.nodes[0]?.id || null;
    const supportingTagLine = node.supporting_tag_line?.value || null;
    const adjustmentPercentage = node.sellingPlanGroups.nodes[0]?.sellingPlans.nodes[0]?.priceAdjustments[0]?.adjustmentValue?.adjustmentPercentage || null;

    const productData = {
      id: node.id.split("/").pop(),
      title: node.title,
      imgUrl: getSizedImageUrl(node.featuredImage?.url, 196, 264),
      imgAltText: node.featuredImage?.altText || node.title || '',
      price: price,
      handle: handle,
      sku: sku,
      variantId: variantId ? variantId.split("/").pop() : null,
      compareAtPrice: compareAtPrice,
      supportingTagLine: supportingTagLine,
      availableForSale: availableForSale,
    };

    if (sellingPlanId) {
      productData.sellingPlanId = sellingPlanId.split("/").pop();
    }

    if (adjustmentPercentage) {
      productData.adjustmentPercentage = adjustmentPercentage;
    }

    formattedProducts.push(productData);

  }

  return formattedProducts;
}

function getSizedImageUrl(src, width, height) {
  if (!src) return '';
  const urlParts = src.split('?'); // separate version param if exists
  const baseUrl = urlParts[0];
  const version = urlParts[1] ? `?${urlParts[1]}` : '';

  // Insert size string with cropping before file extension
  return baseUrl.replace(/(\.(webp|jpg|jpeg|png))$/, `_${width}x${height}_crop_center$1`) + version;
}

export { fetchProducts };